// Sơ đồ Hệ thống bản MÁY TÍNH (đổi 31/10/2026) — đi từng bước theo NHÓM, thay cây ngang mở / gập từng ô:
//  · Ô gốc (NPP / chính mình) nối ra các khối nhóm Đại lý / CTV / Shop; bấm khối → cột đơn vị (8 ô + "Xem thêm 12");
//    bấm đơn vị → các khối nhóm cấp dưới của nó, cứ thế đi sâu. Chỉ có một loại cấp dưới thì mở thẳng nhóm đó.
//  · Mỗi cột chỉ mở một ô (mở ô khác thì nhánh cũ đóng) — bề ngang theo độ sâu, bề cao theo một danh sách.
//  · Đơn vị trong nhóm xếp theo mã (DL01, DL02… — đổi 01/11); chưa có mã thì theo đơn 30 ngày của cả nhánh, nhiều trước.
//  · Đang tìm / lọc: một khối "kết quả" + cột kết quả (trang vẽ thêm đường đi của từng ô), bấm là nhảy vào cây.
//  · Cả khối nằm giữa khung nhìn (chừa ngăn chi tiết); còn rộng thì khe nối dài ra.
// Điện thoại vẫn là cây thụt lề (networkRows); Super Admin vẫn là cây ngang (networkLayout). Phần vẽ:
// apps/web/src/components/network/NetworkCanvas.tsx.

import { networkAncestors, networkCodeOrder, type NetworkNode, type NetworkTree, type NetworkUnitBase, type NetworkUnitType } from './network-tree'

/** Số ô hiện lúc mở nhóm, và số ô mỗi lần bấm "Xem thêm". */
export const NETWORK_DRILL_PAGE = 8
export const NETWORK_DRILL_STEP = 12

const TYPES: readonly NetworkUnitType[] = ['agency', 'ctv', 'shop']

/** Khoá khối kết quả (tìm / lọc). */
export const NETWORK_DRILL_RESULTS = 'g:res'

export const networkDrillGroupKey = (parentKey: string, type: NetworkUnitType) => `g:${parentKey}:${type}`
export const networkDrillUnitKey = (id: string) => `u:${id}`

export interface NetworkDrillState {
  /** Ô đang mở của từng cột từ trái sang: khoá nhóm `g:<cấp trên>:<loại>` và khoá đơn vị `u:<mã>` xen kẽ. */
  path: string[]
  /** Nhóm đã bấm "Xem thêm": số ô đang hiện (không có = NETWORK_DRILL_PAGE). */
  shown: Record<string, number>
}

export interface NetworkDrillIndex<U extends NetworkUnitBase> {
  /** Khoá ô gốc — cũng là khoá cấp trên của đơn vị trực thuộc gốc. */
  rootKey: string
  /** Cấp dưới trực tiếp theo loại, đã xếp. `parentKey` = rootKey hoặc mã đơn vị. */
  kids: (parentKey: string, type: NetworkUnitType) => readonly NetworkNode<U>[]
  /** Đơn 30 ngày cả nhánh (gồm chính nó). */
  act: (node: NetworkNode<U>) => number
}

const belowTotal = (n: NetworkNode<NetworkUnitBase>) => n.below.agency + n.below.ctv + n.below.shop

/** Chia cấp dưới theo loại + xếp: mã (DL01, DL02…) → đơn 30 ngày cả nhánh giảm dần → nhánh đông hơn → tên. */
export function networkDrillIndex<U extends NetworkUnitBase>(
  tree: NetworkTree<U>,
  rootKey: string,
  orders: (unit: U) => number,
): NetworkDrillIndex<U> {
  const act = new Map<NetworkNode<U>, number>()
  const sum = (n: NetworkNode<U>): number => {
    let v = Number(orders(n.unit)) || 0
    for (const c of n.children) v += sum(c)
    act.set(n, v)
    return v
  }
  for (const r of tree.roots) sum(r)
  const cmp = (a: NetworkNode<U>, b: NetworkNode<U>) =>
    networkCodeOrder(a.unit, b.unit) || act.get(b)! - act.get(a)! || belowTotal(b) - belowTotal(a) || a.unit.name.localeCompare(b.unit.name, 'vi')
  const lists = new Map<string, Record<NetworkUnitType, NetworkNode<U>[]>>()
  const put = (key: string, list: readonly NetworkNode<U>[]) => {
    const by: Record<NetworkUnitType, NetworkNode<U>[]> = { agency: [], ctv: [], shop: [] }
    // Loại lạ (dữ liệu lệch) coi như Shop — như networkTypeMeta, không để rơi khỏi sơ đồ
    for (const n of list) (by[n.unit.tenantType] ?? by.shop).push(n)
    for (const t of TYPES) by[t].sort(cmp)
    lists.set(key, by)
  }
  put(rootKey, tree.roots)
  for (const n of tree.byId.values()) if (n.children.length) put(n.unit.id, n.children)
  return { rootKey, kids: (k, t) => lists.get(k)?.[t] ?? [], act: (n) => act.get(n) ?? 0 }
}

const parentKeyOf = <U extends NetworkUnitBase>(ix: NetworkDrillIndex<U>, n: NetworkNode<U>) => (n.parent ? n.parent.unit.id : ix.rootKey)

/** Chỉ có một loại cấp dưới → nhóm đó mở luôn (đỡ một lần bấm). */
export function networkDrillSoleGroup<U extends NetworkUnitBase>(ix: NetworkDrillIndex<U>, parentKey: string): string[] {
  const types = TYPES.filter((t) => ix.kids(parentKey, t).length)
  return types.length === 1 ? [networkDrillGroupKey(parentKey, types[0]!)] : []
}

/** Lúc mới mở trang / sau "Đóng hết". */
export function networkDrillStart<U extends NetworkUnitBase>(ix: NetworkDrillIndex<U>): NetworkDrillState {
  return { path: networkDrillSoleGroup(ix, ix.rootKey), shown: {} }
}

/** Mở tới nhóm chứa `node` ("Xem thêm" đủ để tới nó) — ô của nó hiện, cấp dưới thì chưa. */
export function networkDrillReveal<U extends NetworkUnitBase>(ix: NetworkDrillIndex<U>, node: NetworkNode<U>): NetworkDrillState {
  const chain = [...networkAncestors(node), node]
  const path: string[] = []
  const shown: Record<string, number> = {}
  chain.forEach((n, i) => {
    const pk = parentKeyOf(ix, n)
    const gk = networkDrillGroupKey(pk, n.unit.tenantType)
    path.push(gk)
    const idx = ix.kids(pk, n.unit.tenantType).indexOf(n)
    if (idx >= NETWORK_DRILL_PAGE) shown[gk] = NETWORK_DRILL_PAGE + Math.ceil((idx + 1 - NETWORK_DRILL_PAGE) / NETWORK_DRILL_STEP) * NETWORK_DRILL_STEP
    if (i < chain.length - 1) path.push(networkDrillUnitKey(n.unit.id))
  })
  return { path, shown }
}

/** Như networkDrillReveal, rồi mở luôn cấp dưới của `node` (bấm một kết quả tìm). */
export function networkDrillOpenTo<U extends NetworkUnitBase>(ix: NetworkDrillIndex<U>, node: NetworkNode<U>): NetworkDrillState {
  const s = networkDrillReveal(ix, node)
  if (node.children.length) s.path.push(networkDrillUnitKey(node.unit.id), ...networkDrillSoleGroup(ix, node.unit.id))
  return s
}

/**
 * Bấm ô `key` ở cột `col` (cột 1 = nhóm của gốc): ô đang mở → đóng nhánh của nó; ô khác → mở nó (nhánh anh em
 * đóng). `extra` = mở tiếp sau ô (nhóm duy nhất của đơn vị).
 */
export function networkDrillClick(state: NetworkDrillState, col: number, key: string, extra: readonly string[] = []): NetworkDrillState {
  const i = col - 1
  const path = state.path.slice(0, i)
  if (state.path[i] !== key) path.push(key, ...extra)
  return { path, shown: state.shown }
}

export const networkDrillSame = (a: NetworkDrillState, b: NetworkDrillState) =>
  a.path.length === b.path.length && a.path.every((k, i) => k === b.path[i]) && JSON.stringify(a.shown) === JSON.stringify(b.shown)

// ─── Cột ─────────────────────────────────────────────────────────────────────────────────────────────────────

export type NetworkDrillItem<U extends NetworkUnitBase> =
  | { kind: 'root'; key: string }
  | { kind: 'group'; key: string; type: NetworkUnitType; units: readonly NetworkNode<U>[] }
  | { kind: 'results'; key: string; units: readonly NetworkNode<U>[] }
  /** `result` = ô trong cột kết quả (bấm là nhảy vào cây). */
  | { kind: 'unit'; key: string; node: NetworkNode<U>; result: boolean }
  /** `group` = khoá nhóm (hoặc NETWORK_DRILL_RESULTS); `shown` = số ô đang hiện, `add` = số ô sẽ hiện thêm. */
  | { kind: 'more'; key: string; group: string; shown: number; add: number; left: number }
  | { kind: 'empty'; key: string }

export interface NetworkDrillColumn<U extends NetworkUnitBase> {
  items: NetworkDrillItem<U>[]
  /** Ô đang mở (cột gốc: chính ô gốc). */
  open: NetworkDrillItem<U> | null
}

/**
 * Các cột đang hiện. `results` (đang tìm / lọc) thay cả cây: gốc → khối kết quả → danh sách kết quả.
 * Khoá trong `path` không còn (đơn vị vừa xoá, cây tải lại) thì dừng ở đó.
 */
export function networkDrillColumns<U extends NetworkUnitBase>(
  ix: NetworkDrillIndex<U>,
  state: NetworkDrillState,
  results?: { units: readonly NetworkNode<U>[]; shown: number } | null,
): NetworkDrillColumn<U>[] {
  const root: NetworkDrillItem<U> = { kind: 'root', key: ix.rootKey }
  const cols: NetworkDrillColumn<U>[] = [{ items: [root], open: root }]
  const groups = (pk: string): NetworkDrillItem<U>[] =>
    TYPES.filter((t) => ix.kids(pk, t).length).map((t) => ({ kind: 'group', key: networkDrillGroupKey(pk, t), type: t, units: ix.kids(pk, t) }))
  const page = (key: string, units: readonly NetworkNode<U>[], want: number, result: boolean): NetworkDrillItem<U>[] => {
    const n = Math.min(units.length, Math.max(NETWORK_DRILL_PAGE, want))
    const items: NetworkDrillItem<U>[] = units.slice(0, n).map((node) => ({ kind: 'unit', key: networkDrillUnitKey(node.unit.id), node, result }))
    if (units.length > n) items.push({ kind: 'more', key: `m:${key}`, group: key, shown: n, add: Math.min(NETWORK_DRILL_STEP, units.length - n), left: units.length - n })
    return items
  }

  if (results) {
    const res: NetworkDrillItem<U> = { kind: 'results', key: NETWORK_DRILL_RESULTS, units: results.units }
    cols.push({ items: [res], open: res })
    const items = page(NETWORK_DRILL_RESULTS, results.units, results.shown, true)
    cols.push({ items: items.length ? items : [{ kind: 'empty', key: 'empty' }], open: null })
    return cols
  }

  const first = groups(ix.rootKey)
  if (!first.length) return cols
  cols.push({ items: first, open: null })
  for (const key of state.path) {
    const col = cols[cols.length - 1]!
    const it = col.items.find((x) => x.key === key)
    if (!it) break
    col.open = it
    if (it.kind === 'group') cols.push({ items: page(it.key, it.units, state.shown[it.key] ?? NETWORK_DRILL_PAGE, false), open: null })
    else if (it.kind === 'unit' && it.node.children.length) cols.push({ items: groups(it.node.unit.id), open: null })
    else break
  }
  return cols
}

/** Ô của đơn vị `id` đang hiện trên sơ đồ. */
export function networkDrillShows<U extends NetworkUnitBase>(cols: readonly NetworkDrillColumn<U>[], id: string): boolean {
  const key = networkDrillUnitKey(id)
  return cols.some((c) => c.items.some((it) => it.key === key))
}

// ─── Bố cục ──────────────────────────────────────────────────────────────────────────────────────────────────

export interface NetworkDrillSizes {
  pad: number
  rootW: number
  rootH: number
  groupW: number
  groupH: number
  unitW: number
  unitH: number
  moreH: number
  emptyH: number
  /** Khe giữa hai cột: hẹp nhất / rộng nhất (khung còn rộng thì dài ra tới đây). */
  gapMin: number
  gapMax: number
  /** Khe dọc giữa các khối nhóm / giữa các ô đơn vị; thêm trước ô "Xem thêm". */
  groupGap: number
  unitGap: number
  moreGap: number
}

export const NETWORK_DRILL_SIZES: NetworkDrillSizes = {
  pad: 16, rootW: 184, rootH: 58, groupW: 148, groupH: 68, unitW: 184, unitH: 46, moreH: 34, emptyH: 20,
  gapMin: 24, gapMax: 64, groupGap: 20, unitGap: 8, moreGap: 4,
}

export interface NetworkDrillBox<U extends NetworkUnitBase> {
  item: NetworkDrillItem<U>
  col: number
  x: number
  y: number
  w: number
  h: number
  cy: number
  /** Ô đang mở của cột (khối nhóm / đơn vị trên đường đang xem). */
  open: boolean
}

/**
 * Đoạn nối: `stem` = gạch ngang từ mép phải ô mở tới giữa khe; `down` / `up` = khuỷu từ giữa khe tới mép trái ô cấp
 * dưới (nằm dưới / trên ô mở) — vẽ bằng viền trái + viền dưới / trên, bo góc `radius`.
 */
export interface NetworkDrillLine {
  key: string
  shape: 'stem' | 'down' | 'up'
  x: number
  y: number
  w: number
  h: number
  radius: number
  /** Trên đường đang xem — tô màu nhấn. */
  on: boolean
}

export interface NetworkDrillLayout<U extends NetworkUnitBase> {
  boxes: NetworkDrillBox<U>[]
  /** Đường thường trước, đường tô màu sau (nằm trên). */
  lines: NetworkDrillLine[]
  width: number
  height: number
  /** Khung của cột cuối + ô mở dẫn tới nó — chỗ cần cuộn tới sau mỗi bước. */
  focus: { x: number; y: number; w: number; h: number }
}

/**
 * Xếp các cột vào khung nhìn `view` (`reserveRight` = phần ngăn chi tiết che mép phải):
 *  · ngang: cả khối nằm giữa phần khung còn lại, khe nối dài ra tới gapMax khi còn chỗ, hẹp tới gapMin khi chật
 *    (chật nữa thì tràn phải, khung cuộn ngang);
 *  · dọc: cột nhóm đầu nằm giữa khung, ô gốc giữa các nhóm; cột sau canh giữa theo ô mở của cột trước, không tràn
 *    mép (cột dài hơn khung thì bám mép trên). Cả khối vừa khung thì đưa vào giữa theo chiều dọc.
 */
export function networkDrillLayout<U extends NetworkUnitBase>(
  cols: readonly NetworkDrillColumn<U>[],
  view: { width: number; height: number; reserveRight: number },
  sizes: Partial<NetworkDrillSizes> = {},
): NetworkDrillLayout<U> {
  const o = { ...NETWORK_DRILL_SIZES, ...sizes }
  const widthOf = (ci: number) => (ci === 0 ? o.rootW : ci % 2 ? o.groupW : o.unitW)
  const gapOf = (ci: number) => (ci % 2 ? o.groupGap : o.unitGap)
  const hOf = (it: NetworkDrillItem<U>) =>
    it.kind === 'root' ? o.rootH : it.kind === 'group' || it.kind === 'results' ? o.groupH : it.kind === 'unit' ? o.unitH : it.kind === 'more' ? o.moreH : o.emptyH
  const colH = (ci: number) =>
    cols[ci]!.items.reduce((s, it, j) => s + hOf(it) + (j ? gapOf(ci) + (it.kind === 'more' ? o.moreGap : 0) : 0), 0)

  const viewW = Math.max(0, view.width - view.reserveRight)
  const n = cols.length
  const sumW = cols.reduce((s, _c, ci) => s + widthOf(ci), 0)
  const gap = n > 1 ? Math.max(o.gapMin, Math.min(o.gapMax, Math.floor((viewW - 2 * o.pad - sumW) / (n - 1)))) : o.gapMin
  const blockW = sumW + gap * (n - 1)
  const xs: number[] = []
  let x = o.pad + Math.max(0, Math.round((viewW - 2 * o.pad - blockW) / 2))
  for (let ci = 0; ci < n; ci++) {
    xs.push(x)
    x += widthOf(ci) + gap
  }

  // Mặt phẳng cao bằng khung, hoặc bằng cột dài nhất — cột sau bám ô mở dù ô đó nằm dưới đáy cột dài
  const heights = cols.map((_c, ci) => colH(ci))
  const plane = Math.max(view.height, Math.max(...heights) + 2 * o.pad)
  const fit = (h: number, want: number) => Math.max(o.pad, Math.min(Math.round(want), plane - o.pad - h))

  const boxes: NetworkDrillBox<U>[] = []
  const placed: NetworkDrillBox<U>[][] = []
  const stack = (ci: number, top: number) => {
    const c = cols[ci]!
    let y = top
    placed[ci] = c.items.map((it, j) => {
      if (j) y += gapOf(ci) + (it.kind === 'more' ? o.moreGap : 0)
      const h = hOf(it)
      const b: NetworkDrillBox<U> = { item: it, col: ci, x: xs[ci]!, y, w: widthOf(ci), h, cy: y + h / 2, open: c.open === it }
      y += h
      return b
    })
  }
  if (n > 1) {
    stack(1, fit(heights[1]!, (view.height - heights[1]!) / 2))
    const g = placed[1]!
    const mid = (g[0]!.cy + g[g.length - 1]!.cy) / 2
    stack(0, Math.max(o.pad, Math.round(mid - o.rootH / 2)))
  } else stack(0, fit(o.rootH, (view.height - o.rootH) / 2))
  for (let ci = 2; ci < n; ci++) {
    const par = placed[ci - 1]!.find((b) => b.open)
    stack(ci, fit(heights[ci]!, (par ? par.cy : view.height / 2) - heights[ci]! / 2))
  }
  for (const col of placed) boxes.push(...col)

  let minY = Infinity
  let maxY = -Infinity
  for (const b of boxes) {
    minY = Math.min(minY, b.y)
    maxY = Math.max(maxY, b.y + b.h)
  }
  const dy = maxY - minY <= view.height - 2 * o.pad ? Math.round((view.height - maxY - minY) / 2) : 0
  if (dy) for (const b of boxes) {
    b.y += dy
    b.cy += dy
  }

  const lines: NetworkDrillLine[] = []
  for (let ci = 1; ci < n; ci++) {
    const par = placed[ci - 1]!.find((b) => b.open)
    if (!par) break
    const pr = xs[ci - 1]! + widthOf(ci - 1)
    const mid = pr + gap / 2
    const onKid = cols[ci]!.open
    lines.push({ key: `stem:${par.item.key}`, shape: 'stem', x: pr, y: par.cy, w: gap / 2, h: 0, radius: 0, on: !!onKid })
    for (const b of placed[ci]!) {
      if (b.item.kind === 'more' || b.item.kind === 'empty') continue
      const d = b.cy - par.cy
      const h = Math.abs(d)
      lines.push({
        key: `${par.item.key}>${b.item.key}`,
        shape: d >= 0 ? 'down' : 'up',
        x: mid,
        y: d >= 0 ? par.cy : b.cy,
        w: b.x - mid,
        h,
        radius: Math.min(10, h),
        on: b.item === onKid,
      })
    }
  }
  lines.sort((a, b) => Number(a.on) - Number(b.on))

  let right = 0
  let bottom = 0
  for (const b of boxes) {
    right = Math.max(right, b.x + b.w)
    bottom = Math.max(bottom, b.y + b.h)
  }
  const last = placed[n - 1]!
  const lead = n > 1 ? placed[n - 2]!.find((b) => b.open) : undefined
  const fx = lead ? lead.x : last[0]!.x
  const fy = Math.min(last[0]!.y, lead ? lead.y : Infinity)
  const fb = Math.max(last[last.length - 1]!.y + last[last.length - 1]!.h, lead ? lead.y + lead.h : 0)
  return {
    boxes,
    lines,
    width: right + o.pad + view.reserveRight,
    height: bottom + o.pad,
    focus: { x: fx, y: fy, w: last[0]!.x + last[0]!.w - fx, h: fb - fy },
  }
}
